// ═══════════════════════════════════════════════════════════════
// PROVISIONES — Tab Por Nómina
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import api from '../../../api/config';
import { usePayrollProvisions } from '../../../hooks/useProvisions';
import { provisionsAPI } from '../../../api/provisions';

const fmt = (n) =>
  new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const CODIGO_COLORS = {
  REGAL: 'bg-amber-100 text-amber-700',
  VAC:   'bg-blue-100 text-blue-700',
  CES:   'bg-red-100 text-red-700',
  PREV:  'bg-purple-100 text-purple-700',
};

export default function ProvisionsPayrollView({ companyId }) {
  const [payrolls, setPayrolls]           = useState([]);
  const [payrollsError, setPayrollsError] = useState('');
  const [selectedId, setSelectedId]       = useState('');
  const [searchEmp, setSearchEmp]         = useState('');
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcMsg, setRecalcMsg]         = useState('');

  // Cargar nóminas cerradas de la empresa
  useEffect(() => {
    if (!companyId) return;
    setPayrollsError('');
    api.get(`/payroll/list/${companyId}?estado=closed`)
      .then(r => {
        const list = r.data?.payrolls || r.data || [];
        setPayrolls(list);
        if (list.length === 0) setPayrollsError('No hay nóminas cerradas para esta compañía.');
      })
      .catch(err => {
        console.error('Error cargando nóminas:', err);
        setPayrollsError(`Error: ${err.response?.data?.error || err.message}`);
      });
  }, [companyId]);

  const { detalle, resumen, loading, error, reload } = usePayrollProvisions(selectedId || null);


  const handleRecalculate = async () => {
    if (!selectedId) return;
    setRecalcLoading(true);
    setRecalcMsg('');
    try {
      const r = await provisionsAPI.recalculate(selectedId, companyId);
      setRecalcMsg(`Recalculado: ${r.empleados} empleados actualizados`);
      reload();
    } catch {
      setRecalcMsg('Error al recalcular');
    } finally {
      setRecalcLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Selector de nómina */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Seleccionar Nómina ({payrolls.length} disponibles)
            </label>
            <select
              value={selectedId}
              onChange={e => { setSelectedId(e.target.value); setRecalcMsg(''); }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              <option value="">— Selecciona una nómina cerrada —</option>
              {payrolls.map(p => (
                <option key={p.id} value={p.id}>
                  {p.periodo}{p.quincena ? ` (${p.quincena})` : ''} — {p.tipo} —{' '}
                  {p.employee_count || p.total_empleados || 0} empleados
                </option>
              ))}
            </select>
            {payrollsError && (
              <p className="mt-1 text-xs text-red-500">{payrollsError}</p>
            )}
          </div>

          {selectedId && (
            <button
              onClick={handleRecalculate}
              disabled={recalcLoading}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 shrink-0"
            >
              {recalcLoading ? 'Recalculando...' : '↺ Recalcular'}
            </button>
          )}
        </div>
        {recalcMsg && (
          <p className={`mt-2 text-sm ${recalcMsg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
            {recalcMsg}
          </p>
        )}
      </div>

      {/* Estado */}
      {!selectedId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-400">
          <p className="text-4xl mb-3">📋</p>
          <p className="font-medium">Selecciona una nómina para ver sus provisiones</p>
        </div>
      )}

      {selectedId && loading && <Spinner />}

      {selectedId && error && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <p className="text-amber-700 font-medium">
            Esta nómina aún no tiene provisiones calculadas
          </p>
          <p className="text-sm text-amber-600 mt-1">
            Las provisiones se calculan automáticamente al procesar la nómina.
          </p>
          <button
            onClick={handleRecalculate}
            disabled={recalcLoading}
            className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
          >
            {recalcLoading ? 'Calculando...' : 'Calcular ahora'}
          </button>
        </div>
      )}

      {/* Resumen por tipo */}
      {!loading && resumen.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Resumen de la Nómina</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {resumen.map(r => (
              <div key={r.codigo} className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-center">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CODIGO_COLORS[r.codigo] || 'bg-gray-100 text-gray-600'}`}>
                  {r.codigo}
                </span>
                <p className="text-xs text-gray-500 mt-1 mb-1">{r.nombre}</p>
                <p className="text-lg font-bold text-gray-900">RD$ {fmt(r.total_periodo)}</p>
                <p className="text-xs text-gray-400 mt-0.5">{r.total_empleados} empleados</p>
              </div>
            ))}
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center">
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">TOTAL</span>
              <p className="text-xs text-gray-500 mt-1 mb-1">Todas las provisiones</p>
              <p className="text-lg font-bold text-amber-700">
                RD$ {fmt(resumen.reduce((s, r) => s + parseFloat(r.total_periodo || 0), 0))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Detalle por empleado — una fila por empleado, columnas por tipo */}
      {!loading && detalle.length > 0 && (
        <DetalleTable detalle={detalle} searchEmp={searchEmp} setSearchEmp={setSearchEmp} />
      )}
    </div>
  );
}

// ── Tabla pivote: una fila por empleado, columnas por tipo ─────
function DetalleTable({ detalle, searchEmp, setSearchEmp }) {
  // Obtener codigos únicos en el orden en que aparecen
  const codigos = [...new Set(detalle.map(d => d.codigo))];

  // Agrupar por empleado
  const empleadosMap = {};
  detalle.forEach(d => {
    const key = d.employee_id || d.empleado_codigo;
    if (!empleadosMap[key]) {
      empleadosMap[key] = {
        nombre       : d.empleado_nombre,
        codigo       : d.empleado_codigo,
        salario      : parseFloat(d.salario_mensual || 0),
        meses        : parseFloat(d.meses_servicio  || 0),
        periodos     : {},
        acumulados   : {},
      };
    }
    empleadosMap[key].periodos[d.codigo]   = parseFloat(d.monto_periodo   || 0);
    empleadosMap[key].acumulados[d.codigo] = parseFloat(d.acumulado_nuevo || 0);
  });

  const rows = Object.values(empleadosMap).filter(e =>
    !searchEmp ||
    e.nombre?.toLowerCase().includes(searchEmp.toLowerCase()) ||
    e.codigo?.toLowerCase().includes(searchEmp.toLowerCase())
  );

  // Totales de columna
  const totalPeriodo   = {};
  const totalAcumulado = {};
  codigos.forEach(c => {
    totalPeriodo[c]   = rows.reduce((s, r) => s + (r.periodos[c]   || 0), 0);
    totalAcumulado[c] = rows.reduce((s, r) => s + (r.acumulados[c] || 0), 0);
  });
  const grandTotalPeriodo   = Object.values(totalPeriodo).reduce((s, v) => s + v, 0);
  const grandTotalAcumulado = Object.values(totalAcumulado).reduce((s, v) => s + v, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-base font-semibold text-gray-800">
          Detalle por Empleado ({rows.length} empleados)
        </h2>
        <input
          type="text"
          placeholder="Filtrar empleado..."
          value={searchEmp}
          onChange={e => setSearchEmp(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 w-48"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200 bg-gray-50">
              <th className="text-left py-2.5 pr-4 pl-2 font-semibold text-gray-700 whitespace-nowrap">Empleado</th>
              <th className="text-right py-2.5 px-3 font-semibold text-gray-700">Salario</th>
              <th className="text-right py-2.5 px-3 font-semibold text-gray-700">Meses</th>
              {codigos.map(c => (
                <th key={c} colSpan={2} className="text-center py-2.5 px-2 font-semibold text-gray-700 border-l border-gray-200">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${CODIGO_COLORS[c] || 'bg-gray-100 text-gray-600'}`}>{c}</span>
                </th>
              ))}
              <th colSpan={2} className="text-center py-2.5 px-2 font-semibold text-gray-800 border-l border-gray-300">TOTAL</th>
            </tr>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
              <th className="pl-2 pr-4 pb-2" />
              <th className="pb-2 px-3" />
              <th className="pb-2 px-3" />
              {codigos.map(c => (
                <>
                  <th key={`${c}-p`} className="text-right pb-2 px-2 border-l border-gray-200">Período</th>
                  <th key={`${c}-a`} className="text-right pb-2 px-2">Acum.</th>
                </>
              ))}
              <th className="text-right pb-2 px-2 border-l border-gray-300">Período</th>
              <th className="text-right pb-2 px-2">Acum.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((emp, idx) => {
              const totalP = codigos.reduce((s, c) => s + (emp.periodos[c]   || 0), 0);
              const totalA = codigos.reduce((s, c) => s + (emp.acumulados[c] || 0), 0);
              return (
                <tr key={idx} className="border-b border-gray-50 hover:bg-amber-50 transition-colors">
                  <td className="py-2.5 pr-4 pl-2">
                    <p className="font-medium text-gray-800 whitespace-nowrap">{emp.nombre}</p>
                    <p className="text-xs text-gray-400">{emp.codigo}</p>
                  </td>
                  <td className="py-2.5 px-3 text-right text-gray-600 whitespace-nowrap">{fmt(emp.salario)}</td>
                  <td className="py-2.5 px-3 text-right text-gray-500">{emp.meses.toFixed(1)}</td>
                  {codigos.map(c => (
                    <>
                      <td key={`${c}-p`} className="py-2.5 px-2 text-right text-gray-700 border-l border-gray-100 whitespace-nowrap">
                        {emp.periodos[c] ? fmt(emp.periodos[c]) : '—'}
                      </td>
                      <td key={`${c}-a`} className="py-2.5 px-2 text-right text-gray-500 whitespace-nowrap">
                        {emp.acumulados[c] ? fmt(emp.acumulados[c]) : '—'}
                      </td>
                    </>
                  ))}
                  <td className="py-2.5 px-2 text-right font-semibold text-gray-800 border-l border-gray-200 whitespace-nowrap">
                    {fmt(totalP)}
                  </td>
                  <td className="py-2.5 px-2 text-right font-bold text-amber-700 whitespace-nowrap">
                    {fmt(totalA)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
              <td colSpan={3} className="py-2.5 pl-2 pr-4 text-gray-700">
                Total ({rows.length} empleados)
              </td>
              {codigos.map(c => (
                <>
                  <td key={`${c}-tp`} className="py-2.5 px-2 text-right text-gray-800 border-l border-gray-200 whitespace-nowrap">
                    {fmt(totalPeriodo[c])}
                  </td>
                  <td key={`${c}-ta`} className="py-2.5 px-2 text-right text-gray-600 whitespace-nowrap">
                    {fmt(totalAcumulado[c])}
                  </td>
                </>
              ))}
              <td className="py-2.5 px-2 text-right text-gray-900 border-l border-gray-300 whitespace-nowrap">
                {fmt(grandTotalPeriodo)}
              </td>
              <td className="py-2.5 px-2 text-right text-amber-700 whitespace-nowrap">
                {fmt(grandTotalAcumulado)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
    </div>
  );
}
